import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Activity, Check, ChevronDown, ClipboardCheck, Clock, KeyRound, Pause, PencilLine, Plus, RotateCcw, Settings, UserMinus } from 'lucide-react';
import FinishHomeworkDialog from './FinishHomeworkDialog';
import EditHomeworkChat from './EditHomeworkChat';
import LiveSessionDialog from './LiveSessionDialog';
import ResetPasswordModal from './ResetPasswordModal';
import ResetDiagnosticDialog from './ResetDiagnosticDialog';
import DiagnosticControlDialog from './DiagnosticControlDialog';
import StudentPickerModal from './StudentPickerModal';
import RemoveStudentDialog from './RemoveStudentDialog';
import ManageClassModal from './ManageClassModal';
import NewClassModal from './NewClassModal';
import periodLike from './periodLike';
import useSelectedClass from '../useSelectedClass';
import { chipLabel, checkActionTarget } from '../data/actionRegistry';
import { InlineDialog } from './ui';
import { InlinePanel } from './inlineSlot';
import { Shrinkable } from './Shrinkable';

// The class the chat is about, from the same cached class list the switcher uses.
function useChatClass(classId) {
  const { cls, classes, selectClass } = useSelectedClass();
  const target = (classes || []).find((c) => c.id === classId) || (cls?.id === classId ? cls : null);
  return { target, classes, selectClass };
}
const classTitle = (c) => (c ? [c.period, c.name].filter(Boolean).join(' · ') : undefined);

function RemoveStudentChat({ student, auth, classId, onClose, onDone }) {
  const { target } = useChatClass(classId);
  return <RemoveStudentDialog student={student} auth={auth} classId={classId} className={classTitle(target)} onClose={onClose} onDone={onDone} />;
}

// Manage class opens at the part Buffy named. Each change made in it is
// reported to the chat as it happens (rename, students added or removed,
// co-teachers, delete or leave).
function ManageClassChat({ params, classId, onClose, onDone }) {
  const { target, classes, selectClass } = useChatClass(classId);
  if (!target) return null;
  return <ManageClassModal open cls={target} classes={classes} selectClass={selectClass}
    initialSection={params.section || null} studentQuery={params.query || ''}
    prefill={params.section === 'details' && (params.name || params.period !== undefined) ? { name: params.name, period: params.period } : null}
    onAction={(note) => onDone(note, chipLabel(note.replace(/\.$/, '')))} onClose={onClose} />;
}

// Same dialog and same create as the switcher's New class: the period is
// written the teacher's way, and the new class becomes the selected class on
// the whole page (useSelectedClass shares a pick across instances, F-2).
function NewClassChat({ params, onClose, onDone }) {
  const { selectClass, classes, loading } = useSelectedClass();
  // the dialog reads its period once, so wait for the classes the period is matched to
  if (loading) return null;
  return <NewClassModal open onClose={onClose} selectClass={selectClass} initialName={params.name || ''} initialPeriod={periodLike(params.period, classes)}
    onCreated={(c) => onDone(`Made the class ${classTitle(c)}${c.joinCode ? `; its join code is ${c.joinCode}` : ''}. It is now the selected class.`, `Made ${chipLabel(classTitle(c))}`)} />;
}

/* Chat actions that open the page's own dialog (2026-10-06, Sandy: "anything the
   teacher can do should be achievable in that chat"). Buffy offers the chip;
   clicking it opens a student picker when Buffy didn't name one, then the SAME
   dialog the dashboard page uses, with its own confirm. Nothing runs from the
   chip itself. Secrets typed into a dialog (a temporary password) stay in that
   dialog: onDone only reports what happened, never the values. */
export const DIALOG_ACTIONS = {
  resetPassword: {
    icon: KeyRound,
    pickerTitle: 'Reset a password',
    Dialog: ({ student, auth, classId, onClose, onDone }) => (
      <ResetPasswordModal
        student={student}
        classID={classId}
        auth={auth}
        onClose={onClose}
        onReset={(s) => onDone(`Reset ${s.name}'s password (temporary code shared with the teacher only).`, `Password reset for ${s.name}`)}
      />
    ),
  },
  resetDiagnostic: {
    icon: RotateCcw,
    pickerTitle: 'Reset a diagnostic',
    Dialog: ({ student, params, auth, classId, onClose, onDone }) => (
      <ResetDiagnosticDialog student={student} test={params.test} auth={auth} classId={classId} onClose={onClose} onDone={onDone} />
    ),
  },
  pauseDiagnostic: {
    icon: Pause,
    pickerTitle: 'Pause or unpause a diagnostic',
    Dialog: ({ student, params, auth, classId, onClose, onDone }) => (
      <DiagnosticControlDialog mode="pause" student={student} test={params.test} auth={auth} classId={classId} onClose={onClose} onDone={onDone} />
    ),
  },
  setDiagnosticTime: {
    icon: Clock,
    pickerTitle: 'Set section time',
    Dialog: ({ student, params, auth, classId, onClose, onDone }) => (
      <DiagnosticControlDialog mode="time" student={student} test={params.test} auth={auth} classId={classId} onClose={onClose} onDone={onDone} />
    ),
  },
  // Class-level action: no student picker.
  finishHomework: {
    icon: ClipboardCheck,
    needsStudent: false,
    Dialog: ({ params, auth, classId, onClose, onDone }) => (
      <FinishHomeworkDialog params={params} auth={auth} classId={classId} onClose={onClose} onDone={onDone} />
    ),
  },
  // Edit an assigned set (2026-10-16). Opens at once in the conversation:
  // the box with Buffy's change filled in IS the answer; Save is the confirm.
  editHomework: {
    icon: PencilLine,
    needsStudent: false,
    autoOpen: true,
    Dialog: EditHomeworkChat,
  },
  liveSession: {
    icon: Activity,
    needsStudent: false,
    Dialog: ({ auth, classId, onClose, onDone }) => (
      <LiveSessionDialog auth={auth} classId={classId} onClose={onClose} onDone={onDone} />
    ),
  },
  // Class management (2026-10-13, BBO-82).
  removeStudent: {
    icon: UserMinus,
    pickerTitle: 'Remove a student',
    Dialog: RemoveStudentChat,
  },
  manageClass: {
    icon: Settings,
    needsStudent: false,
    Dialog: ManageClassChat,
  },
  newClass: {
    icon: Plus,
    needsStudent: false,
    Dialog: NewClassChat,
  },
};

// `inline` (2026-10-14): the chip sits inside Buffy's sentence and its picker
// and dialog open right under that paragraph, in the conversation, instead of
// over the page. Same dialog, same confirm. `action.autoOpen` opens it at once
// (the input box's autofill asked for exactly this action).
// A student or set Buffy named is checked against the class first (W4-B,
// review finding 3): the id must be in the roster (or homework list), and
// the name must be that student's. If not, a plain line says so and the
// dialog never opens.
export default function ChatActionChip({ action, auth, classId, docked, onDone, inline = false }) {
  const def = DIALOG_ACTIONS[action.kind];
  const named = action.params.student ? { userId: action.params.student, name: action.params.name || 'this student' } : null;
  const perStudent = def?.needsStudent !== false;
  const needsCheck = !!(named || action.params.homework);
  const firstStep = () => (needsCheck ? 'check' : named || !perStudent ? 'act' : 'pick');
  const [step, setStep] = useState(() => (inline && (action.autoOpen || def?.autoOpen) && def ? (action.refused ? 'refused' : firstStep()) : null)); // null | 'check' | 'refused' | 'pick' | 'act'
  const [student, setStudent] = useState(named);
  const [refusal, setRefusal] = useState(action.refused || null);
  const [doneText, setDoneText] = useState(null);
  useEffect(() => {
    if (step !== 'check') return undefined;
    let alive = true;
    checkActionTarget(auth, classId, action).then((check) => {
      if (!alive) return;
      if (!check.ok) { setRefusal(check.text); setStep('refused'); return; }
      if (check.student) setStudent(check.student);
      setStep(named || !perStudent ? 'act' : 'pick');
    });
    return () => { alive = false; };
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!def) return null;
  const Icon = def.icon;
  // action.refused (prepareSandyAction): the chip can't run here; a click says why.
  const open = () => {
    setStudent(named);
    setRefusal(action.refused || null);
    setStep(action.refused ? 'refused' : firstStep());
  };
  const close = () => setStep(null);
  const finished = (note, label) => { setDoneText(label); onDone?.(note); };
  if (inline) {
    return <>
      <button type="button" className={`bi-chip bi-action is-live${step ? ' is-open' : ''}${doneText ? ' is-done' : ''}`} aria-expanded={!!step}
        title="Opens right here. Nothing happens until you confirm in it." onClick={() => (step ? close() : open())}>
        <span className="bi-ico" aria-hidden="true">{doneText ? <Check /> : <Icon />}</span>
        <span className="bi-label">{doneText || action.text}</span>
        {!doneText && <ChevronDown className="bi-chev" aria-hidden="true" />}
      </button>
      <InlinePanel open={!!step}>
        <InlineDialog.Provider value>
          {step === 'check' && <p className="bi-note" role="status">Checking the class list…</p>}
          {step === 'refused' && <p className="bi-note" role="status">{refusal}</p>}
          {step === 'pick' && <StudentPickerModal auth={auth} classId={classId} title={def.pickerTitle} onClose={close} onPick={(s) => { setStudent(s); setStep('act'); }} />}
          {step === 'act' && (student || !perStudent) && <Shrinkable title={doneText || action.text} label="From Buffy"><def.Dialog key={student?.userId || action.kind} student={student} params={action.params} auth={auth} classId={classId} onClose={close} onDone={finished} /></Shrinkable>}
        </InlineDialog.Provider>
      </InlinePanel>
    </>;
  }
  return (
    <>
      <button
        type="button"
        onClick={open}
        title="Opens the dashboard's own dialog. Nothing happens until you confirm there."
        className={docked
          // Inside .buffy-chat-log (Home, docked panel): the house chips — amber
          // "needs your confirm" until done, then green (buffyChat.css).
          ? `buffy-chip ${doneText ? 'buffy-chip-done' : 'buffy-chip-confirm'}`
          // Floating panel: same meaning in Tailwind. Rings, not `border`:
          // Bootstrap's .border forces its own gray with !important.
          : 'buffy-chip flex items-center gap-1.5 text-[12px] font-bold rounded-full px-3.5 py-1.5 border-0 ring-1 ring-inset cursor-pointer ' +
            (doneText
              ? 'text-success-700 bg-success-50 ring-success-300'
              : 'text-ink bg-white ring-amber-400 hover:bg-amber-50')}
      >
        {doneText ? `✓ ${doneText}` : <><Icon size={docked ? 15 : 12} strokeWidth={docked ? 2 : 2.5} aria-hidden="true" />{action.text}</>}
      </button>
      {step === 'refused' && <span className="buffy-chip-refused" role="status">{refusal}</span>}
      {step === 'pick' && createPortal(
        <StudentPickerModal
          auth={auth}
          classId={classId}
          title={def.pickerTitle}
          onClose={close}
          onPick={(s) => { setStudent(s); setStep('act'); }}
        />,
        document.body,
      )}
      {step === 'act' && (student || !perStudent) && createPortal(
        <def.Dialog key={student?.userId || action.kind} student={student} params={action.params} auth={auth} classId={classId} onClose={close} onDone={finished} />,
        document.body,
      )}
    </>
  );
}
